import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { StyledButton } from "../../../shared/components/buttons/custom-button";
import CustomStepper from "../../../shared/components/stepper/steppers";
import AlertPopup from "../../../shared/components/modals/alert-moadal";
import { LoaderComponent } from "../../../shared/components/loaders/page-loader";
import useNotification from "../../../shared/hooks/useNotification";
import { useApi } from "../../../shared/hooks/useApi";

// Import models
import {
  AlternativeTransactionCertificateDetails,
  ATBuyerSellerDetails,
  ATProductInfoDetails,
  ATRawMaterialInfo,
} from "../atc-models/atc-org";

// Import initial values
import {
  initATCertificateDetails,
  initATBuyerSellerDetails,
  initATProductInfoDetails,
  initATRawMaterialInfo,
} from "../atc-models/atc-initial-values";

// Import step forms
import TransactionCertificateDetailsForm from "./atc-tabs/TransactionCertificateDetailsForm";
import BuyerSellerDetailsForm from "./atc-tabs/BuyerSellerDetailsForm";
import ProductInformationForm from "./atc-tabs/ProductInformationForm";
import RawMaterialInformationForm from "./atc-tabs/RawMaterialInformationForm";

// Service
import { atcService } from "../atc-service/atc.service";
import { ATCUpsertPayload } from "../atc-models/atc-payload";

const AlternativeTransactionCertificate: React.FC = () => {
  // ===================== State =====================
  const [activeStep, setActiveStep] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [atcCertificateDetails, setAtcCertificateDetails] =
    useState<AlternativeTransactionCertificateDetails>(
      initATCertificateDetails
    );
  const [atcBuyerSellerDetails, setAtcBuyerSellerDetails] =
    useState<ATBuyerSellerDetails>(initATBuyerSellerDetails);
  const [atcProductInfoDetails, setAtcProductInfoDetails] = useState<
    ATProductInfoDetails[]
  >([]);
  const [atcRawMaterialInfo, setAtcRawMaterialInfo] = useState<
    ATRawMaterialInfo[]
  >([]);

  const { atcId } = useParams();

  // ===================== Hooks =====================
  const { loading } = useApi();
  const { notify } = useNotification();
  const navigate = useNavigate();

  // ===================== Refs =====================
  const certificateDetailsRef = useRef<any>(null);
  const buyerSellerDetailsRef = useRef<any>(null);
  const productInfoRef = useRef<any>(null);
  const rawMaterialInfoRef = useRef<any>(null);

  const formRefs = [
    certificateDetailsRef,
    buyerSellerDetailsRef,
    productInfoRef,
    rawMaterialInfoRef,
  ];

  // ===================== Prefill on Edit =====================
  useEffect(() => {
    const loadATCData = async () => {
      if (!atcId) {
        // No atcId → use initial empty values
        setAtcCertificateDetails(initATCertificateDetails);
        setAtcBuyerSellerDetails(initATBuyerSellerDetails);
        setAtcProductInfoDetails([]);
        setAtcRawMaterialInfo([]);
        return;
      }

      try {
        const resp = await atcService.getATCDetails(atcId);
        if (resp?.status === 200 && resp?.data) {
          const data: any = resp.data;

          // Prefill certificate details
          setAtcCertificateDetails({
            uploadType: data.uploadType || "",
            transactionCertificateNumber: data.atcNo
              ? data.atcNo.replace(/^ATU-/, "") // removes leading "ATU-"
              : "",
            dateOfIssue: data.atcDateOfIssue || "",
            tcStandard: data.atcStandard ? [data.atcStandard] : [],
            tcVersionNumber: data.atcVersionNo || "",
            cbLicensingCode: data.atcCbLicensingCode || "",
            uploadedFile: null,
            submissionDate: data.atcSubmissionDate || "",
          });

          // Prefill buyer & seller details
          setAtcBuyerSellerDetails({
            sellerName: data.sellerName || "",
            sellerTeId: data.sellerTeid || "",
            sellerAddress: data.sellerAddress || "",
            sellerCbLicensingCode: data.atcCbLicensingCode, // optional
            buyerName: data.buyerName || "",
            buyerTeId: data.buyerTeid || "",
            buyerAddress: data.buyerAddress || "",
            buyerScNumber: data.buyerScNo || "",
          });

          // Prefill products & raw materials
          setAtcProductInfoDetails(
            Array.isArray(data.productInformation)
              ? data.productInformation.map((p: any) => ({
                  productNumber: p.atcProductNo || "",
                  productCategory: p.atcProductCategoryCode || "",
                  productDetail: p.atcProductDetailCode || "",
                  certifiedWeight: String(p.atcCertifiedWeight || ""),
                }))
              : []
          );

          setAtcRawMaterialInfo(
            Array.isArray(data.rawMaterialInformation)
              ? data.rawMaterialInformation.map((r: any) => ({
                  associatedProductNumber: r.associatedProductNo || "",
                  rawMaterialCode: r.rawMaterialCode || "",
                  rawMaterialComposition: r.rawMaterialCompostion || "",
                  countryArea: r.country || "",
                  stateProvince: r.state || "",
                  rawMaterialWeight: String(r.rawMaterialWeight || ""),
                }))
              : []
          );
        } else {
          notify("Failed to fetch ATC details", "error");
        }
      } catch (err) {
        console.error("Error fetching ATC:", err);
        notify("Error fetching ATC details", "error");
      }
    };

    loadATCData();
  }, [atcId]);

  // ===================== Stepper Navigation =====================
  const handleNext = async () => {
    if (activeStep === 0) {
      const isUploaded = certificateDetailsRef.current?.isUploadComplete;
      if (!isUploaded) {
        notify("Please upload the file before proceeding", "error");
        return;
      }
      const errors = certificateDetailsRef.current?.validateForm();
      if (Object.keys(errors || {}).length > 0) {
        notify("Please fill all the required fields", "error");
        return;
      }
    }

    // If we're on Product Information step (index 2), require at least one product row
    if (activeStep === 2) {
      if (!atcProductInfoDetails || atcProductInfoDetails.length === 0) {
        notify("Please add at least one Product Information", "error");
        return;
      }
    }

    // Standard behavior for steps before the last
    if (activeStep < steps.length - 1) {
      const currentRef = formRefs[activeStep]?.current;

      // If ref exists and has a validateForm function, validate first
      if (currentRef && typeof currentRef.validateForm === "function") {
        try {
          const errors = (await currentRef.validateForm()) || {};
          console.log("Validation errors:", errors);
          if (Object.keys(errors).length > 0) {
            notify("Please fill in the required values", "error");
            return;
          }

          // only submit if submitForm exists
          if (typeof currentRef.submitForm === "function") {
            await currentRef.submitForm();
          }

          setActiveStep((prev) => prev + 1);
        } catch (err) {
          console.error("Validation error:", err);
          notify("Validation failed", "error");
        }
      } else {
        // No ref or no validateForm available -> fallback to simply advance
        setActiveStep((prev) => prev + 1);
      }
    } else {
      // Last step behaviour: require at least one raw material row (you already had this)
      if (atcRawMaterialInfo.length > 0) {
        await handleFinish();
      } else {
        notify("Please add at least one Raw Material detail", "error");
      }
    }
  };

 // In the handleFinish function, replace the TC number validation section with this:

const handleFinish = async () => {
  const rawHasUnsaved = Boolean((rawMaterialInfoRef.current as any)?.hasUnsavedRow);
  if (rawHasUnsaved) {
    notify("Please save the current Raw Material row before submitting", "error");
    return;
  }
  if (atcRawMaterialInfo.length === 0) {
    notify("Please add at least one Raw Material Information", "error");
    return;
  }

  const uploadType = atcCertificateDetails.uploadType || "GOTS";
  let atcNumber = atcCertificateDetails.transactionCertificateNumber
    .trim()
    .toUpperCase();

  // Remove any invalid characters (only keep A-Z, 0-9, and dashes)
  atcNumber = atcNumber.replace(/[^A-Z0-9-]/g, "");

  // Validate that uploadType is either ATU or GOTS
  if (!['ATU', 'GOTS'].includes(uploadType)) {
    notify("Upload Type must be either ATU or GOTS", "error");
    return;
  }

  let atcNoNormalized = "";

  // Check if the stored value already starts with the correct upload type
  const uploadPrefix = `${uploadType}-`;
  
  if (atcNumber.startsWith(uploadPrefix)) {
    // Already has the correct prefix, use as-is
    atcNoNormalized = atcNumber;
  } else if (atcNumber.startsWith('ATU-') || atcNumber.startsWith('GOTS-')) {
    // Has a different upload type prefix, replace it
    const withoutPrefix = atcNumber.substring(atcNumber.indexOf('-') + 1);
    atcNoNormalized = `${uploadType}-${withoutPrefix}`;
  } else {
    // No upload type prefix, add it
    atcNoNormalized = `${uploadType}-${atcNumber}`;
  }

  // Final validation: ensure it starts with ATU or GOTS and contains only valid characters
  if (!atcNoNormalized.match(/^(ATU|GOTS)-[A-Z0-9-]+$/)) {
    notify(
      "Transaction Certificate Number format is invalid. It must start with ATU or GOTS followed by a dash and contain only letters, numbers, and dashes.",
      "error"
    );
    return;
  }

  // Additional check: ensure it's not just the prefix
  if (atcNoNormalized === `${uploadType}-` || atcNoNormalized.length <= uploadType.length + 1) {
    notify(
      "Transaction Certificate Number must contain content after the upload type prefix.",
      "error"
    );
    return;
  }

  // ✅ Check certified weight vs raw material total
  const weightMismatch: string[] = [];
  atcProductInfoDetails.forEach((product) => {
    const productNo = product.productNumber;
    const certified = Number(product.certifiedWeight) || 0;

    // sum raw materials for this product
    const rawTotal = atcRawMaterialInfo
      .filter((rm) => rm.associatedProductNumber === productNo)
      .reduce((sum, rm) => sum + (Number(rm.rawMaterialWeight) || 0), 0);

    if (certified !== rawTotal) {
      weightMismatch.push(
        `Product ${productNo}: Certified ${certified}, Raw Total ${rawTotal}`
      );
    }
  });

  if (weightMismatch.length > 0) {
    notify(`Weight mismatch found:\n${weightMismatch.join("\n")}`, "error");
    return; // ❌ Stop submission
  }

  // --- continue with your existing payload build ---
  const plain = (label?: string) =>
    label ? label.replace(/\s*\(.*\)$/, "") : "";

  const apiPayload: ATCUpsertPayload = {
    atcNo: atcNoNormalized, // use the normalized number
    atcVersionNo: atcCertificateDetails.tcVersionNumber,
    uploadType: atcCertificateDetails.uploadType,
    atcCbLicensingCode: atcCertificateDetails.cbLicensingCode,
    atcStandard: atcCertificateDetails.tcStandard[0] || "",
    atcDateOfIssue: atcCertificateDetails.dateOfIssue,
    atcSubmissionDate:
      atcCertificateDetails.submissionDate ||
      atcCertificateDetails.dateOfIssue,
    blobPath: atcCertificateDetails.uploadedFile?.name || "",
    sellerName: atcBuyerSellerDetails.sellerName,
    sellerTeid: atcBuyerSellerDetails.sellerTeId,
    sellerAddress: atcBuyerSellerDetails.sellerAddress,
    buyerName: atcBuyerSellerDetails.buyerName,
    buyerTeid: atcBuyerSellerDetails.buyerTeId,
    buyerAddress: atcBuyerSellerDetails.buyerAddress,
    buyerScNo: atcBuyerSellerDetails.buyerScNumber,
    productInformation: atcProductInfoDetails.map((p) => ({
      atcProductNo: p.productNumber,
      atcProductCategoryCode: p.productCategory,
      atcProductDetailCode: p.productDetail,
      atcCertifiedWeight: parseFloat(String(p.certifiedWeight)) || 0.0,
    })),
    rawMaterialInformation: atcRawMaterialInfo.map((r) => ({
      associatedProductNo: r.associatedProductNumber,
      rawMaterialCode: r.rawMaterialCode,
      rawMaterialCompostion:
        parseFloat(String(r.rawMaterialComposition)) || 0.0,
      country: Array.isArray(r.countryArea)
        ? r.countryArea
        : r.countryArea
        ? [r.countryArea]
        : [],

      countryCode: Array.isArray(r.countryArea)
        ? r.countryArea
        : r.countryArea
        ? [r.countryArea]
        : [],

      state: Array.isArray(r.stateProvince)
        ? r.stateProvince
        : r.stateProvince
        ? [r.stateProvince]
        : [],

      stateCode: Array.isArray(r.stateProvince)
        ? r.stateProvince
        : r.stateProvince
        ? [r.stateProvince]
        : [],
      rawMaterialWeight: parseFloat(String(r.rawMaterialWeight)) || 0.0,
    })),
  };

  try {
    const resp = await atcService.saveATCDetails(apiPayload);
    if (resp?.status === 200 || resp?.status === 201) {
      notify("ATC Details Saved Successfully", "success");
      navigate("/atc");
    } else {
      const errorMsg =
        (resp?.data as any)?.response || "Failed to save ATC details";
      notify(errorMsg, "error");
  
      // 👇 Navigate to Step 1 if message includes "Current Standard"
      if (typeof errorMsg === "string" && errorMsg.includes("Current Standard")) {
        setActiveStep(0);
      }
    }
  } catch (error: any) {
    console.error("Error saving ATC details:", error);
    const errorMsg =
      error?.response?.data?.response ||
      error?.response?.data?.message ||
      "Failed to save ATC details";
  
    notify(errorMsg, "error");
  
    // 👇 Same condition for caught errors
    if (typeof errorMsg === "string" && errorMsg.includes("Current Standard")) {
      setActiveStep(0);
    }
  }
  
};

const handleBack = () => {
  // Check for unsaved rows in Raw Material Information (step 3)
  if (activeStep === 3) {
    const rawHasUnsaved = Boolean((rawMaterialInfoRef.current as any)?.hasUnsavedRow);
    if (rawHasUnsaved) {
      notify("Please save or cancel the current Raw Material row before navigating back", "error");
      return;
    }
  }
  // Check for unsaved rows in Product Information (step 2)
  if (activeStep === 2) {
    const productHasUnsaved = Boolean((productInfoRef.current as any)?.hasUnsavedRow);
    if (productHasUnsaved) {
      notify("Please save or cancel the current Product Information row before navigating back", "error");
      return;
    }
  }
  setActiveStep((prev) => Math.max(prev - 1, 0));
};

  const atcProductNum = atcProductInfoDetails.map(
    (prod) => prod?.productNumber
  );

  const productWeightsMap = atcProductInfoDetails.reduce((acc, product) => {
    const weight = Number(product.certifiedWeight) || 0; // convert string to number
    acc[product.productNumber] = weight;
    return acc;
  }, {} as Record<string, number>);

  // ===================== Steps =====================
  const steps = [
    {
      label: "Transaction Certificate Details",
      content: (
        <TransactionCertificateDetailsForm
          ref={certificateDetailsRef}
          detailsData={atcCertificateDetails}
          setDetailsData={setAtcCertificateDetails}
        />
      ),
    },
    {
      label: "Buyer & Seller Details",
      content: (
        <BuyerSellerDetailsForm
          formRef={buyerSellerDetailsRef}
          detailsData={atcBuyerSellerDetails}
          setDetailsData={setAtcBuyerSellerDetails}
        />
      ),
    },
    {
      label: "Product Information",
      content: (
        <ProductInformationForm
          formRef={productInfoRef}
          detailsData={atcProductInfoDetails}
          setDetailsData={setAtcProductInfoDetails}
        />
      ),
    },
    {
      label: "Raw Material Information",
      content: (
        <RawMaterialInformationForm
          formRef={rawMaterialInfoRef}
          detailsData={atcRawMaterialInfo}
          setDetailsData={setAtcRawMaterialInfo}
          productNumbers={atcProductNum}
          productWeightsMap={productWeightsMap}
        />
      ),
    },
  ];

  // ===================== Actions =====================
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  // Minimal gating: prevent navigation when there are unsaved edits in steps 2/3,
  // or no saved rows on those steps. Do not change other behavior.
  const productHasUnsaved = Boolean(
    (productInfoRef.current as any)?.hasUnsavedRow
  );
  const rawHasUnsaved = Boolean(
    (rawMaterialInfoRef.current as any)?.hasUnsavedRow
  );
  const nextDisabled =
    (activeStep === 2 &&
      (productHasUnsaved ||
        !atcProductInfoDetails ||
        atcProductInfoDetails.length === 0)) ||
    (activeStep === 3 &&
      (rawHasUnsaved ||
        !atcRawMaterialInfo ||
        atcRawMaterialInfo.length === 0));

  const actions = [
    ...(!isFirstStep
      ? [
          {
            label: "Back",
            onClick: handleBack,
            variant: "contained" as const,
            color: "inherit" as const,
          },
        ]
      : []),
      ...(isFirstStep
        ? [
            {
              label: "Cancel",
              onClick: () => setDialogOpen(true),
              variant: "outlined" as const,
              color: "secondary" as const,
            },
          ]
        : []),
    {
      label: isLastStep ? "Submit" : "Next",
      onClick: isLastStep ? handleFinish : handleNext,
      variant: "contained" as const,
      color: "primary" as const,
      disabled: nextDisabled,
    },
  ];

  useEffect(() => {
    if (activeStep === 1 && buyerSellerDetailsRef.current) {
      const ref = buyerSellerDetailsRef.current;

      // Seller and Buyer Names: disable if value exists
      const sellerNameDisabled = !!atcBuyerSellerDetails.sellerName;
      const buyerNameDisabled = !!atcBuyerSellerDetails.buyerName;

      ref.setDisable?.({
        sellerName: sellerNameDisabled,
        sellerAddress: !!atcBuyerSellerDetails.sellerAddress,
        sellerCbLicensingCode: false, // always editable
        buyerName: buyerNameDisabled,
        buyerAddress: !!atcBuyerSellerDetails.buyerAddress,
        buyerScNumber: false, // always editable
      });

      ref.setVisible?.({
        sellerName: !atcBuyerSellerDetails.sellerName,
        buyerName: !atcBuyerSellerDetails.buyerName,
        sellerTeIdLabel: !atcBuyerSellerDetails.sellerTeId,
        buyerTeIdLabel: !atcBuyerSellerDetails.buyerTeId,
      });
    }
  }, [activeStep, atcBuyerSellerDetails]);

  // ===================== Cancel =====================
  const handleCancelSave = () => setDialogOpen(false);

  const handleCancelAmendment = async () => {
    // Uncomment below for cancel API
    /*
    try {
      await atcService.cancelATC(ATC_ID);
      notify("Cancelled Successfully", "success");
      navigate("/atc-dashboard");
    } catch (err) {
      notify("Failed to cancel", "error");
    }
    */

    notify("Cancelled Successfully", "success");
    navigate("/atc");
    setDialogOpen(false);
  };

  // ===================== Render =====================
  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={(step: number) => setActiveStep(step)}
          />

          <Box
            sx={{
              marginTop: "3rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {actions.map((action, index) => (
              <StyledButton
                key={index}
                onClick={action.onClick}
                variant={action.variant || "text"}
                disabled={false}
                color={action.color}
              >
                {action.label}
              </StyledButton>
            ))}
          </Box>

          <AlertPopup
            open={dialogOpen}
            title="Cancel Confirmation"
            message="Are you sure you want to cancel?"
            onClose={handleCancelSave}
            onConfirm={handleCancelAmendment}
            confirmText="Yes, Cancel"
            cancelText="No"
          />
        </>
      )}
    </>
  );
};
export default AlternativeTransactionCertificate;
